import { ReactNode, useCallback, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { runOrToast } from "@/utils/runOrToast";

import {
	BlacklistOverrideResult,
	BlacklistOverrideVerdict,
} from "@/pages/Private/pages/Blacklist/schema/blacklist-override";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { isLeadBlacklisted } from "@/pages/Private/pages/List/utils/isLeadBlacklisted";
import {
	useApplyBlacklistOverrideMutation,
	usePreviewBlacklistOverrideMutation,
} from "@/pages/Private/redux/blacklist/blacklist-override.api";

import {
	BlacklistOverridePlan,
	BlacklistOverrideWording,
	leadsAllowedAfterOverride,
	planBlacklistOverride,
	showSkippedLeadsToast,
} from "./blacklistOverride";
import { BlacklistOverrideModal } from "./BlacklistOverrideModal";

/** The columns of a lead the gate reads: its id, and what `isLeadBlacklisted` decides on. */
export type BlacklistGateLead = Pick<
	ListPerson,
	"id" | "inclusionStatus" | "blacklistType" | "prospect"
>;

/** What the user agreed to in the modal, handed to the action that goes on. */
export interface BlacklistOverrideConsent {
	/** Also remove the company-wide entries that block a lead. */
	removeCompany: boolean;
}

export interface BlacklistOverrideGateOptions {
	/**
	 * `"contact"` (default): the gate takes the leads off the blacklist itself, then lets the action
	 * go on. `"remove"`: the action's own request takes them off (a bulk status move, which applies
	 * the same rules server-side), so the gate only asks and hands the consent over.
	 */
	wording?: BlacklistOverrideWording;
	/** A line about the action's scope, shown under the lists. */
	note?: string;
}

export type BlacklistOverrideProceed<T> = (
	allowed: T[],
	consent: BlacklistOverrideConsent
) => unknown;

interface BlacklistOverrideGate {
	/**
	 * Lets an action that contacts (or un-blacklists) `leads` go on, asking first when any of them
	 * is blacklisted. Resolves `true` once `proceed` ran — with the leads it may now touch, never a
	 * refused or skipped one — and `false` when the user cancelled, nothing could be taken off, or
	 * a request failed (the failure is toasted). A rejected `proceed` rejects, as it would without
	 * the gate.
	 */
	gate: <T extends BlacklistGateLead>(
		leads: T[],
		proceed: BlacklistOverrideProceed<T>,
		options?: BlacklistOverrideGateOptions
	) => Promise<boolean>;
	/** Render this wherever the caller renders its own modals. */
	overrideModal: ReactNode;
}

interface PromptState {
	plan: BlacklistOverridePlan;
	wording: BlacklistOverrideWording;
	note?: string;
	removeCompany: boolean;
	busy: boolean;
}

/**
 * The one place a blacklisted lead is let through to an action that contacts it (PREC-2548). Every
 * entry point — the reach-out picker, the lead panel's Reach out, the chat's Send, the list
 * table's status moves — asks through here, so they cannot disagree about who may be contacted.
 *
 * A selection with no blacklisted lead goes straight through, without a request or a modal. For
 * the rest the API is asked what taking each lead off would take (`blacklist-override/preview`),
 * and the answer is put to the user; nothing is removed before they confirm.
 */
export const useBlacklistOverrideGate = (): BlacklistOverrideGate => {
	const { t } = useTranslation();
	const [preview] = usePreviewBlacklistOverrideMutation();
	const [apply] = useApplyBlacklistOverrideMutation();

	const [prompt, setPrompt] = useState<PromptState | null>(null);
	const resolveConsentRef = useRef<((consent: BlacklistOverrideConsent | null) => void) | null>(
		null
	);

	const previewVerdicts = useCallback(
		async (listPersonIds: number[]): Promise<BlacklistOverrideVerdict[] | null> => {
			const answer: { verdicts?: BlacklistOverrideVerdict[] } = {};
			const ok = await runOrToast(async () => {
				answer.verdicts = await preview({ listPersonIds }).unwrap();
			}, t("blacklist.override.previewFailed"));

			return ok ? (answer.verdicts ?? []) : null;
		},
		[preview, t]
	);

	const askUser = useCallback(
		(plan: BlacklistOverridePlan, wording: BlacklistOverrideWording, note?: string) =>
			new Promise<BlacklistOverrideConsent | null>((resolve) => {
				// A gate still waiting for an answer is answered as cancelled, never left hanging.
				resolveConsentRef.current?.(null);
				resolveConsentRef.current = resolve;
				setPrompt({ plan, wording, note, removeCompany: false, busy: false });
			}),
		[]
	);

	// The ids the API actually took off; null when the request failed (and was toasted).
	const applyOverride = useCallback(
		async (
			plan: BlacklistOverridePlan,
			consent: BlacklistOverrideConsent
		): Promise<number[] | null> => {
			const answer: { result?: BlacklistOverrideResult } = {};
			const ok = await runOrToast(async () => {
				answer.result = await apply({
					listPersonIds: plan.overridable.map((verdict) => verdict.listPersonId),
					...(consent.removeCompany && { removeCompany: true }),
				}).unwrap();
			}, t("blacklist.override.applyFailed"));

			if (!ok) {
				return null;
			}

			showSkippedLeadsToast(answer.result?.skipped, t);

			return answer.result?.overriddenIds ?? [];
		},
		[apply, t]
	);

	const gate = useCallback(
		async <T extends BlacklistGateLead>(
			leads: T[],
			proceed: BlacklistOverrideProceed<T>,
			options: BlacklistOverrideGateOptions = {}
		): Promise<boolean> => {
			const wording = options.wording ?? "contact";
			const blacklistedIds = leads.filter((lead) => isLeadBlacklisted(lead)).map(({ id }) => id);

			if (blacklistedIds.length === 0) {
				await proceed(leads, { removeCompany: false });

				return true;
			}

			const verdicts = await previewVerdicts(blacklistedIds);

			if (!verdicts) {
				return false;
			}

			const plan = planBlacklistOverride(verdicts);
			const consent = await askUser(plan, wording, options.note);

			if (!consent) {
				return false;
			}

			// A status move takes the leads off itself; here they are only the ones it may touch.
			const clearedIds =
				wording === "contact"
					? await applyOverride(plan, consent)
					: plan.overridable.map((verdict) => verdict.listPersonId);

			setPrompt(null);

			const allowed = clearedIds
				? leadsAllowedAfterOverride(leads, blacklistedIds, clearedIds)
				: [];

			if (allowed.length === 0) {
				return false;
			}

			await proceed(allowed, consent);

			return true;
		},
		[previewVerdicts, askUser, applyOverride]
	);

	const answer = (consent: BlacklistOverrideConsent | null) => {
		resolveConsentRef.current?.(consent);
		resolveConsentRef.current = null;
	};

	const cancel = () => {
		answer(null);
		setPrompt(null);
	};

	const confirm = () => {
		if (!prompt) {
			return;
		}

		// Busy until the gate closes it: Cancel, Escape and the backdrop cannot decide again while
		// the override is being applied (web rule §23).
		setPrompt({ ...prompt, busy: true });
		answer({ removeCompany: prompt.removeCompany });
	};

	const toggleRemoveCompany = () =>
		setPrompt((current) => current && { ...current, removeCompany: !current.removeCompany });

	const overrideModal = prompt ? (
		<BlacklistOverrideModal
			busy={prompt.busy}
			note={prompt.note}
			plan={prompt.plan}
			removeCompany={prompt.removeCompany}
			wording={prompt.wording}
			onCancel={cancel}
			onConfirm={confirm}
			onToggleRemoveCompany={toggleRemoveCompany}
		/>
	) : null;

	return { gate, overrideModal };
};
